// @file For all whiteboard global state and logic
import { trackEvent } from '@@/bits/analytics'
import { isTouchScreen } from '@@/bits/browser'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { getVuexStore } from '@@/bits/pinia'
import type { TldrawFontId } from '@@/bits/whiteboard'
import {
  addFrame,
  deleteFrame as deleteFrameHelper,
  FRAME_ID_QUERY_PARAM,
  getTldrawFontId,
  MAX_FRAME_LIMIT,
  moveFrame,
} from '@@/bits/whiteboard'
import { VIEWER_TOOL_IDS } from '@@/bits/whiteboard_permissions'
import type { CONNECTOR_COLORS, TldrawDefaultColorStyleColor } from '@@/bits/whiteboard_tools'
import { arrowToolColors, DEFAULT_COLORS, geoToolColors, getAllTools } from '@@/bits/whiteboard_tools'
import anxiousFace from '@@/images/anxious_face.svg'
import { OzConfirmationDialogBoxButtonScheme } from '@@/library/v4/components/OzConfirmationDialogBox.vue'
import { useGlobalAlertDialogStore } from '@@/pinia/global_alert_dialog'
import { useGlobalConfirmationDialogStore } from '@@/pinia/global_confirmation_dialog'
import { SnackbarNotificationType, useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { UpgradeSource, UpgradeStep, usePersonalPlansStore } from '@@/pinia/personal_plans_store'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceContainerSizeStore } from '@@/pinia/surface_container_size'
import { useWhiteboardCardTemplatesStore } from '@@/pinia/whiteboard_card_templates'
import { useWhiteboardModeStore } from '@@/pinia/whiteboard_mode'
import { useWhiteboardPermissionsStore } from '@@/pinia/whiteboard_permissions'
import type { Frame, WhiteboardSecondaryMenu, WhiteboardTool } from '@@/types/whiteboard'
import { useCopyToClipboard } from '@@/vuecomposables/copy_to_clipboard'
import { useTldrawEditor } from '@@/vuecomposables/useTldrawEditor'
import { useWhiteboardToolActions } from '@@/vuecomposables/whiteboard_tool_actions'
import type { RootState } from '@@/vuexstore/surface/types'
import { defineStore } from 'pinia'
import type { TLPageId, TLShape } from 'tldraw'
import { computed, ref } from 'vue'

export const useWhiteboardStore = defineStore('whiteboard', () => {
  const whiteboardCardTemplatesStore = useWhiteboardCardTemplatesStore()
  const whiteboardPermissionStore = useWhiteboardPermissionsStore()
  const whiteboardModeStore = useWhiteboardModeStore()
  const surfaceStore = useSurfaceStore()
  const { copyToClipboard } = useCopyToClipboard()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const personalPlansStore = usePersonalPlansStore()
  const globalAlertDialogStore = useGlobalAlertDialogStore()
  const globalConfirmationDialogStore = useGlobalConfirmationDialogStore()

  /**
   * SHAPE APPROVAL
   */
  const unapprovedShapes = ref<TLShape[]>([])
  const numUnapprovedShapes = computed(() => {
    return unapprovedShapes.value.length
  })
  const timesUnapprovedShapesUpdated = ref(0)

  /** FRAMES */
  // #region FRAMES
  const frames = ref<Frame[]>([])
  const currentFrameId = ref<TLPageId | null>(null)
  const previousFrameId = ref<TLPageId | null>(null)
  const xFrameBackgroundPanel = ref(false)
  const xFrameSharePanel = ref(false)
  const xFrameShareViaAnotherAppPanel = ref(false)
  const xFrameBreakoutLinksPanel = ref(false)
  const frameBarHeight = ref(0)

  useTldrawEditor().react((editor) => {
    if (currentFrameId.value !== editor.getCurrentPageId()) {
      // When switching between modes, this react is run and the currentFrameId is updated again to be the same
      // Guard it so that it doesn't set previousFrameId to be the same as currentFrameId
      previousFrameId.value = currentFrameId.value
      currentFrameId.value = editor.getCurrentPageId()

      // For some reason, if we call this method the first time `currentFrameId` is populated, it causes a bug
      // where a visitor can't add shape after they load the sandbox until they change to a different tool and
      // change it back again. I don't understand how that's the case because we also have other watchers that
      // call this method. For now, let's just block it from running the first time by checking that `previousFrameId`
      // is already non-null (the first time, `previousFrameId` is null).
      if (previousFrameId.value != null) {
        useWhiteboardToolActions().selectFirstAllowedTool()
      }
    }
  })

  const backgroundDominantColorInRgba = computed(() => {
    const opacity = 0.2
    const dominantColor = surfaceStore.background?.dominant_color
    if (dominantColor == null || dominantColor.length === 0) {
      return surfaceStore.backgroundLuminance === 'dark'
        ? `rgba(14, 13, 9, ${opacity})`
        : `rgba(229, 219, 202, ${opacity})`
    }
    return `rgba(${dominantColor.join(',')}, ${opacity})`
  })
  const frameBarAndToolbarBackground = computed(() => {
    return surfaceStore.backgroundLuminance === 'dark'
      ? `linear-gradient(0deg, ${backgroundDominantColorInRgba.value} 0%, ${backgroundDominantColorInRgba.value} 100%), linear-gradient(0deg, rgba(255, 255, 255, 0.10) 0%, rgba(255, 255, 255, 0.10) 100%), rgba(0, 0, 0, 0.70)`
      : `linear-gradient(0deg, ${backgroundDominantColorInRgba.value} 0%, ${backgroundDominantColorInRgba.value} 100%), linear-gradient(0deg, rgba(0, 0, 0, 0.05) 0%, rgba(0, 0, 0, 0.05) 100%), rgba(255, 255, 255, 0.70)`
  })

  function setFrameBarHeight(height: number): void {
    frameBarHeight.value = height
  }

  // This is used for background/share panel since the current frame isn't always the frame that the user opened a panel for
  const panelFrameId = ref<TLPageId>()

  const getFrameIndex = (frameId: string): number => frames.value.findIndex((frame) => frame.id === frameId)

  const isSingleFrame = computed(() => frames.value.length === 1)
  function isFirstFrame(frameId: string): boolean {
    return frames.value[0]?.id === frameId
  }
  function isLastFrame(frameId: string): boolean {
    return frames.value[frames.value.length - 1]?.id === frameId
  }

  function getCanAddFrame(): boolean {
    if (!whiteboardPermissionStore.canIManage || surfaceStore.isFrozen) {
      return false
    }
    if (hasReachedPlanFrameLimitAndCannotUpgrade.value) {
      trackEvent('Whiteboard', 'Cannot add frame because of plan limit')
      globalAlertDialogStore.openAlertDialog({
        iconSrc: anxiousFace,
        title: __('Frame limit reached'),
        body: __('This sandbox has a limit of %{frameLimit} frames.', {
          frameLimit: surfaceStore.whiteboardFrameLimit,
        }),
        xShadow: true,
      })
      return false
    }
    if (shouldShowUpgradeModal.value) {
      trackEvent('Whiteboard', 'Shown upgrade modal after trying to add frame')
      if (surfaceStore.isLibraryWall) {
        // TODO: For future handling of libraries
        // This code block won't execute for now because of hasReachedPlanFrameLimitAndCannotUpgrade
      } else {
        personalPlansStore.userTriggeredUpgrade({
          user: surfaceStore.user,
          upgradeSource: UpgradeSource.WhiteboardAddFrameClick,
          step: UpgradeStep.ChooseTierFrameQuota,
        })
      }

      return false
    }

    return true
  }

  function addFrameIfPossible(frameIndex: number): void {
    const canAddFrame = getCanAddFrame()
    if (canAddFrame) {
      addFrame(frameIndex)

      // After adding frame, suggest card templates
      if (isAppUsing('sandboxCardTemplates')) {
        whiteboardCardTemplatesStore.xSuggestedCardTemplatesRow = true
        whiteboardCardTemplatesStore.xStartFromATemplateButton = true
      }
    }
  }

  function addFrameAbove(frameId: string): void {
    const frameIndex = getFrameIndex(frameId)
    // We supply the same index because when adding a frame above, we want the new frame to take the index of the current frame
    addFrameIfPossible(frameIndex)
  }
  function addFrameBelow(frameId: string): void {
    const frameIndex = getFrameIndex(frameId)
    addFrameIfPossible(frameIndex + 1)
  }

  function moveFrameUp(frameId: TLPageId): void {
    const indexOfFrameToMove = getFrameIndex(frameId)

    moveFrame(frameId, indexOfFrameToMove, indexOfFrameToMove - 1)
  }
  function moveFrameDown(frameId: TLPageId): void {
    const indexOfFrameToMove = getFrameIndex(frameId)

    moveFrame(frameId, indexOfFrameToMove, indexOfFrameToMove + 1)
  }

  function moveFrameAbove({ frameId, targetFrameId }: { frameId: TLPageId; targetFrameId: TLPageId }): void {
    const indexOfFrameToMove = getFrameIndex(frameId)
    const indexOfTargetFrame = getFrameIndex(targetFrameId)

    if (indexOfFrameToMove < indexOfTargetFrame) {
      moveFrame(frameId, indexOfFrameToMove, indexOfTargetFrame - 1)
    }

    if (indexOfFrameToMove > indexOfTargetFrame) {
      moveFrame(frameId, indexOfFrameToMove, indexOfTargetFrame)
    }
  }

  function moveFrameBelow({ frameId, targetFrameId }: { frameId: TLPageId; targetFrameId: TLPageId }): void {
    const indexOfFrameToMove = getFrameIndex(frameId)
    const indexOfTargetFrame = getFrameIndex(targetFrameId)

    if (indexOfFrameToMove < indexOfTargetFrame) {
      moveFrame(frameId, indexOfFrameToMove, indexOfTargetFrame)
    }

    if (indexOfFrameToMove > indexOfTargetFrame) {
      moveFrame(frameId, indexOfFrameToMove, indexOfTargetFrame + 1)
    }
  }

  function deleteFrame(frameId: TLPageId): void {
    if (isSingleFrame.value) {
      globalAlertDialogStore.openAlertDialog({
        title: __('Card cannot be deleted'),
        body: __('At least one card is required. Please create a new card if you want to delete this one.'),
        closeButtonText: __('OK'),
        shouldFadeIn: true,
      })
      return
    }

    globalConfirmationDialogStore.openConfirmationDialog({
      title: __('Delete this card?'),
      body: __('Are you sure you want to delete this card? This cannot be undone!'),
      confirmButtonText: __('Delete'),
      cancelButtonText: __('Nevermind'),
      buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
      afterConfirmActions: [() => deleteFrameHelper(frameId)],
    })
  }

  // #endregion FRAMES

  /**
   * FRAME LIMITS
   * */

  const hasReachedPlanFrameLimit = computed(() => frames.value.length >= surfaceStore.whiteboardFrameLimit)
  const hasReachedMaximumFrameLimit = computed(() => frames.value.length >= MAX_FRAME_LIMIT)
  const canUpgradeToIncreaseFrameLimit = computed<boolean>(() => {
    if (hasReachedMaximumFrameLimit.value) {
      return false
    }
    if (surfaceStore.isLibraryWall) {
      // TODO: Support upgrade for library trial
      return false
    }
    return surfaceStore.isBuilderCurrentUser
  })
  const shouldShowUpgradeModal = computed(() => hasReachedPlanFrameLimit.value && canUpgradeToIncreaseFrameLimit.value)
  const hasReachedPlanFrameLimitAndCannotUpgrade = computed<boolean>(
    () => hasReachedPlanFrameLimit.value && !canUpgradeToIncreaseFrameLimit.value,
  )

  /**
   * FRAME URLS
   */
  // #region FRAME URLS
  const getLinkToFrame = (frameId: TLPageId): string => {
    const linkToFrame = new URL(surfaceStore.links.show)
    linkToFrame.searchParams.append(FRAME_ID_QUERY_PARAM, frameId)

    return linkToFrame.toString()
  }

  const copyLinkToFrame = (frameId: TLPageId): void => {
    void copyToClipboard({
      text: getLinkToFrame(frameId),
      globalSnackbarOptions: {
        message: __('Link copied to clipboard'),
        notificationType: SnackbarNotificationType.success,
        timeout: 1500,
      },
    })
  }

  const copyFrameBreakoutLink = (frameId: TLPageId): void => {
    const frameBreakoutLink = frameBreakoutLinks.value[frameId]
    if (frameBreakoutLink === '') {
      globalSnackbarStore.setSnackbar({
        message: __('There was an error creating the breakout room. Please try again'),
        notificationType: SnackbarNotificationType.error,
      })
      return
    }

    void copyToClipboard({
      text: frameBreakoutLink,
      globalSnackbarOptions: {
        message: __('Link copied to clipboard'),
        notificationType: SnackbarNotificationType.success,
        timeout: 1500,
      },
    })
  }

  // #endregion FRAME URLS

  /** FRAME BREAKOUT LINKS */
  // #region FRAME BREAKOUT LINKS
  // We use wallHashid and not public key because we don't want users to be able to go back to the original wall.
  // Format: {origin}/{namespace}/card-breakout-room/{wallHashidWithoutPrefix}-{frameId}
  // E.g. padlet.com/username/card-breakout-room/wallHashidWithoutPrefix-frameId
  const frameBreakoutOriginPrefix = computed(() => {
    const namespace: string = surfaceStore.namespace
    const origin = new URL(window.location.href).origin
    return `${origin}/${namespace}`
  })
  const frameBreakoutLinkPrefix = computed(() => `${frameBreakoutOriginPrefix.value}/card-breakout-link`)
  const frameBreakoutRoomPrefix = computed(() => `${frameBreakoutOriginPrefix.value}/card-breakout-room`)
  // Support backwards compatibility with old card-breakout-link in the url
  const isFrameBreakout = computed(() => {
    const frameBreakoutPrefixes = [frameBreakoutLinkPrefix.value, frameBreakoutRoomPrefix.value]
    return frameBreakoutPrefixes.some((prefix) => window.location.href.includes(prefix))
  })

  /**
   * Format: {origin}/{namespace}/card-breakout-room/{wallHashidWithoutPrefix}-{breakoutFrameId}
   * Use this value to get the current breakout room's frame ID instead of `currentFrameId`
   * if you need it before Tldraw renders.
   */
  const breakoutFrameId = computed<TLPageId | null>(() => {
    if (!isFrameBreakout.value) return null
    const frameBreakoutPrefixes = [frameBreakoutLinkPrefix.value, frameBreakoutRoomPrefix.value]
    const prefix = frameBreakoutPrefixes.find((prefix) => window.location.href.includes(prefix))
    if (prefix == null) return null
    const identifier = window.location.href.split(prefix)[1]
    const frameId = identifier?.split(`/${surfaceStore.wallHashidWithoutPrefix}-`).pop()
    if (frameId == null) return null
    return frameId as TLPageId
  })

  const frameBreakoutLinks = computed<{ [frameId: TLPageId]: string }>(() => {
    // We don't want any _ in the url
    // Example format of wallHashid: 'board_mockRandomHash'
    const wallHashidWithoutPrefix: string = surfaceStore.wallHashidWithoutPrefix
    const frameIdToBreakoutLinkMapping: { [frameId: string]: string } = {}

    frames.value.forEach((frame) => {
      frameIdToBreakoutLinkMapping[frame.id] = `${frameBreakoutRoomPrefix.value}/${wallHashidWithoutPrefix}-${frame.id}`
    })
    return frameIdToBreakoutLinkMapping
  })

  // #endregion FRAME BREAKOUT LINKS

  /** TOOLS & SELECTED TOOL */
  // #region TOOLS & SELECTED TOOL
  const selectedToolbarButtonEl = ref(null)

  function setSelectedToolbarButtonEl(el): void {
    selectedToolbarButtonEl.value = el
  }

  const tools = ref<WhiteboardTool[]>(
    getAllTools().map((tool) => ({
      ...tool,
    })),
  )

  const toolStates = ref<
    Record<string, { currentSecondarySelectionId?: string; fillStyle?: 'none' | 'semi' | 'solid' }>
  >(
    getAllTools().reduce(
      (acc, tool) => ({ ...acc, [tool.id]: { currentSecondarySelectionId: tool.secondaryMenu?.defaultSelection } }),
      {},
    ),
  )

  const toolCurrentColors = computed<Record<string, string | undefined>>(() => {
    const result: Record<string, string | undefined> = {}

    Object.keys(toolStates.value).forEach((toolId) => {
      const toolState = toolStates.value[toolId]
      if (toolId === 'draw-nested' && toolState.currentSecondarySelectionId != null) {
        result[toolId] = toolStates.value[toolState.currentSecondarySelectionId].currentSecondarySelectionId
        return
      }
      if (toolId === 'geo') {
        result[toolId] = geoColorMenuSelectionId.value
        return
      }

      if (toolId === 'arrow') {
        result[toolId] = arrowColorMenuSelectionId.value
        return
      }

      if (toolState.currentSecondarySelectionId != null) {
        result[toolId] = toolState.currentSecondarySelectionId
      }
    })

    return result
  })

  const isFirstTimeSelectingTool = ref(true)
  const selectedToolId = ref<string>('select')

  const selectedTool = computed(() => {
    return tools.value.find((tool) => tool.id === selectedToolId.value)
  })

  function selectTool(toolId: string): void {
    if (currentFrameId.value == null) return
    if (toolId === selectedToolId.value) return
    if (!whiteboardPermissionStore.isToolAllowed(toolId, currentFrameId.value)) return
    selectedToolId.value = toolId

    // Dismiss suggested card templates row when selecting a tool.
    // Only do it for subsequent times, not the first time (because we call `selectTool`
    // after Tldraw is mounted).
    if (isAppUsing('sandboxCardTemplates') && !isFirstTimeSelectingTool.value) {
      whiteboardCardTemplatesStore.dismissCardTemplatesSuggestionUI()
    }

    isFirstTimeSelectingTool.value = false
  }

  function selectDefaultTool(): void {
    const tldrawEditor = useTldrawEditor().editor
    if (tldrawEditor == null) {
      return
    }

    const isEmptyCanvas = tldrawEditor.getCurrentPageShapeIds().size === 0
    const isWriteableEmptyCanvas =
      whiteboardPermissionStore.canIWriteFrame(tldrawEditor.getCurrentPageId()) && isEmptyCanvas

    let defaultToolId: string

    if (whiteboardModeStore.isReadOnlyMode) {
      defaultToolId = VIEWER_TOOL_IDS[0]
    } else if (isWriteableEmptyCanvas) {
      defaultToolId = isTouchScreen() ? 'draw-nested' : 'text'
    } else {
      defaultToolId = 'select'
    }

    selectTool(defaultToolId)
  }

  /**
   * Sometimes we want to change the selected tool without triggering the tldraw tool change
   * This is useful when we want to change the tool but not the tldraw tool
   * Find usage by searching for `selectToolWithoutTriggeringTldraw`
   */
  const ignoreTldrawTrigger = ref(false)
  function selectToolWithoutTriggeringTldraw(toolId: string): void {
    selectedToolId.value = toolId
    ignoreTldrawTrigger.value = true
  }

  function selectSecondaryTool(secondaryToolId: string): void {
    // Check if the selected tool has a secondary menu and the selected secondary tool is valid
    if (selectedTool.value?.secondaryMenu?.items == null) return
    if (!selectedTool.value.secondaryMenu.items.some((item) => item.id === secondaryToolId)) return

    toolStates.value = {
      ...toolStates.value,
      [selectedToolId.value]: { currentSecondarySelectionId: secondaryToolId },
    }
  }

  // Selected color or shape tool
  const selectedSecondaryTool = computed(() => {
    return selectedTool.value?.secondaryMenu?.items.find(
      (item) => item.id === toolStates.value?.[selectedToolId.value].currentSecondarySelectionId,
    )
  })

  // const selectedTertiaryTool = computed(() => {
  //   return selectedSecondaryTool.value?.secondaryMenu?.items.find(
  //     (item) => item.id === selectedSecondaryTool.value?.state.currentSecondarySelectionId,
  //   )
  // })

  // #endregion TOOLS & SELECTED TOOL

  /** GEOMETRY COLOR TOOLBAR */
  // #region GEOMETRY COLOR TOOLBAR
  // Geo color Menu
  const geoColorMenu = ref<WhiteboardSecondaryMenu | null>({
    id: 'color',
    items: geoToolColors,
    defaultSelection: DEFAULT_COLORS.shapeBorder,
  })

  const geoColorMenuSelectionId = ref<string>(DEFAULT_COLORS.shapeBorder)

  function selectGeoColor(colorId: string): void {
    if (geoColorMenu.value?.items.some((item) => item.id === colorId) == null) return
    geoColorMenuSelectionId.value = colorId
  }

  const geoColorTldrawToolId = computed<TldrawDefaultColorStyleColor | undefined>(
    () =>
      geoColorMenu.value?.items.find((item) => item.id === geoColorMenuSelectionId.value)?.tldrawToolId as
        | TldrawDefaultColorStyleColor
        | undefined,
  )

  // #endregion GEOMETRY COLOR TOOLBAR

  /** ARROW COLOR TOOLBAR */
  // #region ARROW COLOR TOOLBAR
  // Arrow color Menu
  const arrowColorMenu = ref<WhiteboardSecondaryMenu | null>({
    id: 'color',
    items: arrowToolColors,
    defaultSelection: DEFAULT_COLORS.connector,
  })

  const arrowColorMenuSelectionId = ref<typeof CONNECTOR_COLORS[number]>(DEFAULT_COLORS.connector)

  function selectArrowColor(colorId: typeof CONNECTOR_COLORS[number]): void {
    arrowColorMenuSelectionId.value = colorId
  }

  const arrowColorTldrawToolId = computed<TldrawDefaultColorStyleColor | undefined>(
    () =>
      arrowColorMenu.value?.items.find((item) => item.id === arrowColorMenuSelectionId.value)?.tldrawToolId as
        | TldrawDefaultColorStyleColor
        | undefined,
  )

  // #endregion ARROW COLOR TOOLBAR

  /** GLOBAL ACTION */

  const canIUndo = ref(false)
  const canIRedo = ref(false)

  /**  */
  // #region Whiteboard shared state
  const isLoadingPortraitMobileLayout = computed(() => {
    if (useSurfaceContainerSizeStore().isLandscapePhone || useSurfaceContainerSizeStore().isLandscapeTablet) {
      return false
    }
    return useSurfaceContainerSizeStore().isContainerSmallerThanDesktop
  })

  // #endregion Embeds
  const whiteboardEmbedCodeUrlTemplate = computed(
    () => getVuexStore<RootState>()?.state.constants.whiteboardEmbedCodeUrlTemplate,
  )
  // #endregion

  // #endregion Fonts
  const defaultTldrawFontId = computed<TldrawFontId>(() => getTldrawFontId(surfaceStore.fontId) ?? 'draw')
  // #endregion

  return {
    /** FRAMES */
    // State
    frames,
    previousFrameId,
    currentFrameId,
    xFrameBackgroundPanel,
    xFrameSharePanel,
    xFrameShareViaAnotherAppPanel,
    frameBreakoutLinks,
    xFrameBreakoutLinksPanel,
    isFrameBreakout,
    breakoutFrameId,
    panelFrameId,
    hasReachedPlanFrameLimitAndCannotUpgrade,
    isLoadingPortraitMobileLayout,
    numUnapprovedShapes,
    unapprovedShapes,
    timesUnapprovedShapesUpdated,
    backgroundDominantColorInRgba,
    frameBarAndToolbarBackground,
    frameBarHeight,
    setFrameBarHeight,

    // Actions
    isSingleFrame,
    isFirstFrame,
    isLastFrame,
    moveFrameUp,
    moveFrameDown,
    moveFrameAbove,
    moveFrameBelow,
    addFrameAbove,
    addFrameBelow,
    getFrameIndex,
    deleteFrame,
    copyLinkToFrame,
    copyFrameBreakoutLink,
    getLinkToFrame,
    getCanAddFrame,

    /** TOOLS */
    // State
    tools,
    toolStates,
    toolCurrentColors,
    selectedTool,
    selectedToolId,
    selectedSecondaryTool,
    // selectedTertiaryTool,
    selectedToolbarButtonEl,
    ignoreTldrawTrigger,

    // Actions
    selectTool,
    selectDefaultTool,
    selectToolWithoutTriggeringTldraw,
    selectSecondaryTool,
    setSelectedToolbarButtonEl,

    /** GEOMETRY COLOR TOOLBAR */
    // State
    geoColorMenu,
    geoColorMenuSelectionId,
    geoColorTldrawToolId,

    // Action
    selectGeoColor,

    /** ARROW COLOR TOOLBAR */
    // State
    arrowColorMenu,
    arrowColorMenuSelectionId,
    arrowColorTldrawToolId,

    // Action
    selectArrowColor,

    /** TOOLTIP */

    /** GLOBAL ACTION */
    canIUndo,
    canIRedo,

    /** EMBEDS */
    whiteboardEmbedCodeUrlTemplate,

    /** FONTS */
    defaultTldrawFontId,
  }
})
